import {
  Alert,
  Box,
  ButtonBase,
  Collapse,
  Dialog,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/CheckCircleOutlined";
import WarnIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InfoIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import { useEffect, useState } from "react";
import type { AssetPlaybackBlocker } from "@shared/channel-playability";
import type { Availability } from "@shared/content-rights";
import { imageIssues, imageSrc, type ImagesInput } from "@shared/vod-images";
import {
  localisedIssues,
  localisedStatus,
  type LocalisedInput,
} from "@shared/vod-localised";
import { foyerApi, type IngestJobView } from "../../http/foyer";
import { ingestHoldExplanation } from "@shared/ingest-gate";
import AvailabilityChip from "./AvailabilityChip";
import { blockerText, imageIssueText, localisedIssueText } from "./findings";
import { tokens } from "../../theme";

/**
 * Översikt — "is this title ready?", answered in one screen (`BE-32`).
 *
 * ## Nothing here is computed here
 *
 * Every line is one of the derivations that already exists —
 * `assetPlaybackBlockers`, `imageIssues`, `localisedIssues`, `localisedStatus`,
 * `availabilityFor`, `packageMembership`, the ingest job's own phase — rendered
 * through `findings.ts`, which is the only place in the CMS where a code
 * becomes a sentence. A second wording of the same finding is how a summary
 * starts quietly disagreeing with the editor beside it, and the operator then
 * has to decide which screen to believe.
 *
 * `blockers` and `availability` are PASSED IN rather than recomputed, from the
 * same `useMemo`s the editor's own rail draws. Recomputing them from the same
 * draft would give the same answer today and is exactly the seam along which
 * two screens drift.
 *
 * ## The line nobody thinks to check
 *
 * **Packages.** A title can be transcoded, delivered, inside its licence
 * window, with artwork, in nine languages and with every ingest phase green —
 * and if no package carries it, no guest can see it. There is no error state
 * for that anywhere else in the product: it is the one failure that looks
 * exactly like success. It is therefore the first row on this tab, not the
 * last.
 */

type Tone = "ok" | "warn" | "info";

function RowIcon({ tone }: { tone: Tone }) {
  if (tone === "ok")
    return <CheckIcon sx={{ fontSize: 18, color: tokens.success }} />;
  if (tone === "warn")
    return <WarnIcon sx={{ fontSize: 18, color: tokens.warning }} />;
  return <InfoIcon sx={{ fontSize: 18, color: tokens.inkFaint }} />;
}

function Row({
  tone,
  label,
  value,
  detail,
  aside,
  children,
}: {
  tone: Tone;
  label: string;
  value: string;
  detail?: string[];
  aside?: React.ReactNode;
  /** Rendered under the detail lines — the accordion body, when there is one. */
  children?: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      sx={{ py: 1, borderBottom: `1px solid ${tokens.divider}` }}
      data-testid={`overview-${label.toLowerCase().replace(/\W+/g, "-")}`}
    >
      <Box sx={{ pt: 0.25 }}>
        <RowIcon tone={tone} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.ink }}>
            {label}
          </Typography>
          {aside}
        </Stack>
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {value}
        </Typography>
        {detail?.map((line, i) => (
          <Typography
            key={i}
            sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.25 }}
          >
            • {line}
          </Typography>
        ))}
        {children}
      </Box>
    </Stack>
  );
}

export default function AssetOverviewTab({
  assetId,
  asset,
  blockers,
  availability,
  jobs,
  packagePicker,
}: {
  assetId: string;
  /**
   * The draft as an asset — what the editor beside this is holding.
   *
   * Structural, exactly like the inputs `imageIssues`, `localisedIssues` and
   * `availabilityFor` take: `draftAsAsset` produces a shape with the
   * collections and none of the server-owned identity fields, and demanding a
   * full `Asset` here would force a caller to invent them.
   */
  asset: ImagesInput & LocalisedInput;
  blockers: AssetPlaybackBlocker[];
  availability: Availability;
  jobs: IngestJobView[] | null;
  /**
   * Lets the Packages row be OPENED and answered, rather than only read.
   *
   * ⚠️ It does not pin anything. Pinning is `$addToSet` on approval in
   * `contentSubmissionStore`, and that line calls itself "THE gate — nothing
   * else in Foyer may pin unscreened content, and this is the line that makes
   * an approval mean something". A picker that wrote directly would be the
   * second path that gate exists to prevent, reachable from a drawer whose
   * whole subject is a title nobody has screened yet.
   *
   * So this is the CHOICE, held by the caller and applied by whatever the
   * caller already does with it — in the review drawer, the approval. Absent,
   * the row stays read-only, which is what the staff editor wants: there,
   * packages are edited on the Packages page against the package itself.
   */
  packagePicker?: {
    options: Array<{ id: string; name: string }>;
    value: string[];
    onChange: (ids: string[]) => void;
  };
}) {
  const [packages, setPackages] = useState<Awaited<
    ReturnType<typeof foyerApi.listAssetPackages>
  > | null>(null);
  const [packagesFailed, setPackagesFailed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [artworkOpen, setArtworkOpen] = useState(false);
  /**
   * The picture being looked at properly, or null.
   *
   * A 140px thumbnail is enough to see THAT an image is there and nothing
   * about what is in it — which is the whole reason this row exists. Judging
   * a poster at that size is the same mistake as judging it by its dimensions.
   */
  const [zoomed, setZoomed] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    foyerApi
      .listAssetPackages(assetId)
      .then((a) => live && setPackages(a))
      // A failure must NOT read as "no packages". "I do not know" and "nobody"
      // are the same sentence to a reader and opposite facts — the delete
      // guard's rule, and it matters more here where the wrong answer looks
      // like a diagnosis.
      .catch(() => live && setPackagesFailed(true));
    return () => {
      live = false;
    };
  }, [assetId]);

  const images = imageIssues(asset);
  const locIssues = localisedIssues(asset);
  const localisedRows = asset.localizedInfo ?? [];
  const doneLanguages = localisedRows.filter(
    (row) => localisedStatus(row) === "done"
  ).length;
  const live = jobs?.find(
    (j) => j.phase !== "completed" && j.phase !== "failed"
  );
  /**
   * 🚨 A HELD job is not a running one.
   *
   * `live` is "not completed and not failed", which is true of a job sitting on
   * a hold — so this row said **"An ingest is running — queued"** about a title
   * the dispatcher will never pick up (`leaseNextQueued` filters on `hold`).
   * Nothing is running and nothing will until a person acts.
   *
   * The same lie the supplier's own ladder told (`70d3d01`), on the staff side
   * of the same job. Worth noticing that one wrong reading of `phase` produced
   * it on both surfaces independently: `phase` answers "where has this job
   * got to", never "is anything happening to it".
   */
  const held = live?.hold ?? null;
  const lastJob = jobs?.[0];

  const members = packages?.packages.filter((p) => p.member) ?? [];
  const excluded =
    packages?.packages.filter((p) => p.reason === "excluded") ?? [];

  return (
    <Box data-testid="asset-overview">
      {/* First, because it is the only failure that looks like success. */}
      {packagesFailed ? (
        <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
          Could not read which packages carry this title. That is not the same
          as “none” — do not treat it as one.
        </Alert>
      ) : (
        <Row
          tone={members.length > 0 ? "ok" : packages === null ? "info" : "warn"}
          label="Packages"
          value={
            packages === null
              ? "Checking…"
              : members.length > 0
                ? `Carried by ${members.length} of ${packages.totalPackages}.`
                : `In none of ${packages.totalPackages} packages — no guest can see this title, whatever else is green.`
          }
          detail={[
            ...members.map(
              (p) =>
                `${p.name} — ${p.reason === "pinned" ? "pinned" : "carried by the rule"}`
            ),
            ...excluded.map((p) => `${p.name} — deliberately excluded`),
          ]}
          aside={
            packagePicker ? (
              <IconButton
                size="small"
                aria-label={
                  pickerOpen ? "Hide the package picker" : "Choose packages"
                }
                onClick={() => setPickerOpen((v) => !v)}
                data-testid="overview-packages-toggle"
                sx={{
                  p: 0,
                  color: tokens.brand,
                  transform: pickerOpen ? "rotate(180deg)" : "none",
                  transition: "transform 120ms",
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            ) : undefined
          }
        >
          {packagePicker && (
            <Collapse in={pickerOpen} unmountOnExit>
              <TextField
                select
                size="small"
                fullWidth
                sx={{ mt: 1 }}
                SelectProps={{ multiple: true }}
                label="Approve into"
                value={packagePicker.value}
                onChange={(e) =>
                  packagePicker.onChange(
                    typeof e.target.value === "string"
                      ? e.target.value.split(",")
                      : (e.target.value as unknown as string[])
                  )
                }
                inputProps={{ "data-testid": "overview-packages-select" }}
                /* Says WHEN it takes effect. The row above reports what the
                   catalogue holds now; this is a decision that lands on
                   approval, and conflating the two is how somebody picks a
                   package and wonders why the count did not move. */
                helperText="Applied when you approve. A title in no package plays for nobody."
              >
                {packagePicker.options.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Collapse>
          )}
        </Row>
      )}

      <Row
        tone={blockers.length === 0 ? "ok" : "warn"}
        label="Playable"
        value={
          blockers.length === 0
            ? "There is something a guest can select and play."
            : `${blockers.length} thing${blockers.length === 1 ? "" : "s"} stop this playing.`
        }
        detail={blockers.map(blockerText)}
      />

      <Row
        // `unrestricted` is NOT a decision anybody made — it is the import
        // default — so it is not drawn as a tick. The chip carries the
        // five-state nuance; the tone here only says "look at this or not".
        tone={availability.state === "available" ? "ok" : "warn"}
        label="Availability"
        value={
          availability.availableIn.length > 0
            ? `Licensed in ${availability.availableIn.join(", ")}.`
            : "No market is licensed right now."
        }
        aside={<AvailabilityChip availability={availability} />}
      />

      <Row
        tone={doneLanguages > 0 ? "ok" : "warn"}
        label="Languages"
        value={
          localisedRows.length === 0
            ? "No localised text at all — the device falls back to the original title."
            : `${doneLanguages} of ${localisedRows.length} complete.`
        }
        detail={locIssues.map(localisedIssueText)}
      />

      <Row
        tone={images.length === 0 ? "ok" : "warn"}
        label="Artwork"
        value={`${(asset.images ?? []).length} image${(asset.images ?? []).length === 1 ? "" : "s"}.`}
        detail={images.map(imageIssueText)}
        aside={
          (asset.images ?? []).length > 0 ? (
            <IconButton
              size="small"
              aria-label={artworkOpen ? "Hide the artwork" : "Show the artwork"}
              onClick={() => setArtworkOpen((v) => !v)}
              data-testid="overview-artwork-toggle"
              sx={{
                p: 0,
                color: tokens.brand,
                transform: artworkOpen ? "rotate(180deg)" : "none",
                transition: "transform 120ms",
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : undefined
        }
      >
        {/**
         * 🚨 A reviewer approves what they have SEEN.
         *
         * Every other row on this tab reports a count or a rule — facts a
         * machine can check. Artwork is the one thing on a title that can be
         * wrong in a way no validator will ever catch: a poster can satisfy
         * every dimension and still be something we would not put on a screen
         * in a care home. Counting the images and calling that a check is how
         * an unscreened picture reaches a television.
         *
         * So the pictures themselves are one click away, in the drawer where
         * the approval happens, rather than two panels deeper in an editor the
         * reviewer has no reason to open.
         */}
        {(asset.images ?? []).length > 0 && (
          <Collapse in={artworkOpen} unmountOnExit>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
              data-testid="overview-artwork-previews"
            >
              {(asset.images ?? []).map((image, i) => {
                const src = imageSrc(image, { width: 200 });
                const caption = [image.type, image.language]
                  .filter(Boolean)
                  .join(" · ");
                // Falls back to 16:9 only when the record carries no
                // dimensions — an unmeasured image is already reported as an
                // issue on this very row, so the guess is visible, not hidden.
                const ratio =
                  image.width && image.height
                    ? `${image.width} / ${image.height}`
                    : "16 / 9";
                return (
                  <Box key={i} sx={{ width: 140 }}>
                    {/* ButtonBase, not a Box with onClick: this has to be
                        reachable and operable from the keyboard like any other
                        control, and it announces itself to a screen reader. */}
                    <ButtonBase
                      onClick={() => setZoomed(i)}
                      aria-label={`Open ${caption || "image"} full size`}
                      data-testid={`overview-artwork-open-${i}`}
                      sx={{ display: "block", borderRadius: 1, width: 140 }}
                    >
                      {/**
                       * 🚨 Each picture keeps its OWN shape.
                       *
                       * A fixed 16:9 box cropped a portrait poster to a
                       * horizontal slice of itself — which is worse than
                       * useless on a row whose only job is letting a reviewer
                       * see what the picture actually is. The six image types
                       * here are not one shape: posters are portrait, covers
                       * and backdrops landscape.
                       *
                       * The ratio comes from the stored dimensions so the
                       * space is reserved before the bytes arrive and the row
                       * does not jump as they load; `contain` then keeps it
                       * honest if those dimensions ever disagree with the file.
                       */}
                      <Box
                        component={src ? "img" : "div"}
                        src={src ?? undefined}
                        alt={caption || "image"}
                        data-ratio={ratio}
                        sx={{
                          width: 140,
                          aspectRatio: ratio,
                          objectFit: "contain",
                          borderRadius: 1,
                          display: "block",
                          background: tokens.divider,
                          border: `1px solid ${tokens.divider}`,
                          transition: "border-color 120ms",
                          "&:hover": { borderColor: tokens.brand },
                        }}
                      />
                    </ButtonBase>
                    <Typography
                      sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.25 }}
                    >
                      {caption}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Collapse>
        )}

        {/**
         * Full size, on top of everything.
         *
         * Asked for at a width the scaler will actually serve rather than the
         * stored original: these are masters, and a 4000px poster downloaded
         * to be looked at once is a cost with no benefit.
         */}
        {/**
         * 🚨 `maxWidth={false}`, and the paper sized here.
         *
         * With MUI's `lg` the paper caps at ~1200px while the picture inside
         * was sized against the VIEWPORT — so on a wide screen the image was
         * simply wider than the sheet holding it and its right-hand edge was
         * clipped off. A lightbox that hides part of the picture is worse than
         * no lightbox: the reviewer believes they have seen the whole thing.
         *
         * So the paper is the constraint and the image fills it, rather than
         * the two being measured against different things.
         */}
        <Dialog
          open={zoomed !== null}
          onClose={() => setZoomed(null)}
          maxWidth={false}
          data-testid="overview-artwork-lightbox"
          PaperProps={{
            sx: {
              maxWidth: "min(1600px, 92vw)",
              maxHeight: "92vh",
              m: 2,
              overflow: "hidden",
            },
          }}
        >
          {zoomed !== null &&
            (() => {
              const image = (asset.images ?? [])[zoomed];
              if (!image) return null;
              const big = imageSrc(image, { width: 1600 });
              const caption = [image.type, image.language]
                .filter(Boolean)
                .join(" · ");
              return (
                <Box sx={{ position: "relative", lineHeight: 0 }}>
                  <IconButton
                    onClick={() => setZoomed(null)}
                    aria-label="Close"
                    data-testid="overview-artwork-lightbox-close"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: "rgba(0,0,0,.55)",
                      color: "#fff",
                      "&:hover": { background: "rgba(0,0,0,.75)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Box
                    component={big ? "img" : "div"}
                    src={big ?? undefined}
                    alt={caption || "image"}
                    sx={{
                      display: "block",
                      // Relative to the PAPER, not the viewport — the whole
                      // point of the fix above.
                      width: "100%",
                      height: "auto",
                      maxHeight: "82vh",
                      objectFit: "contain",
                      background: tokens.divider,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: tokens.inkMuted,
                      p: 1,
                      lineHeight: 1.4,
                    }}
                  >
                    {caption}
                    {image.width && image.height
                      ? ` — ${image.width}×${image.height}`
                      : ""}
                  </Typography>
                </Box>
              );
            })()}
        </Dialog>
      </Row>

      <Row
        tone={
          live
            ? "info"
            : lastJob?.phase === "completed"
              ? "ok"
              : lastJob?.phase === "failed"
                ? "warn"
                : "info"
        }
        label="Media files"
        value={
          jobs === null
            ? "Checking…"
            : jobs.length === 0
              ? "Nothing has been ingested for this title."
              : held
                ? held.reason === "awaiting_review"
                  ? "Ingest has not started — waiting for review."
                  : "Ingest has not started — the submission is incomplete."
                : live
                  ? `An ingest is running — ${live.phase}.`
                  : lastJob?.phase === "completed"
                    ? "The last ingest published."
                    : `The last ingest ${lastJob?.phase ?? "did not finish"}.`
        }
        /**
         * The consequence comes from `ingestHoldExplanation`, shared with the
         * supplier shell, because "a hold that is described two ways is a hold
         * somebody escalates twice". Only the short line above is written for
         * this reader; the operative sentence stays single-sourced.
         */
        detail={
          held
            ? [ingestHoldExplanation(held.reason)]
            : lastJob?.error
              ? [lastJob.error]
              : undefined
        }
      />
    </Box>
  );
}
